import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, IconButton } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

const HERO_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=800', alt: 'Premium Dry Fruits' },
  { src: 'https://images.unsplash.com/photo-1636816571948-121db9832798?q=80&w=1606&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', alt: 'Mixed Nuts' },
  { src: 'https://plus.unsplash.com/premium_photo-1676208748373-a31d5af9f752?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', alt: 'Fresh Nuts & Dried Fruits' },
];

const HeroBanner = () => {
  const navigate = useNavigate();
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f5ebe0 0%, #d5c4a1 100%)',
        minHeight: { xs: '400px', md: '500px' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          right: { xs: '-100px', md: '5%' },
          top: '50%',
          transform: 'translateY(-50%)',
          width: { xs: '300px', md: '450px' },
          height: { xs: '300px', md: '450px' },
          borderRadius: '50%',
          background: 'rgba(139, 69, 19, 0.1)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
          }}
        >
          <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, textAlign: { xs: 'center', md: 'left' }, zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#8B4513',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block',
              }}
            >
              Premium Quality
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: '#3E2723',
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2,
              }}
            >
              Nature's Best <br />
              <span style={{ color: '#8B4513' }}>Dry Fruits & Nuts</span>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#5D4037',
                mb: 4,
                fontWeight: 400,
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              Handpicked premium quality nuts and dry fruits delivered fresh to your doorstep.
              Experience the goodness of nature.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/filter/All')}
                sx={{
                  bgcolor: '#8B4513',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#6d3610' },
                }}
              >
                Shop Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  borderColor: '#8B4513',
                  color: '#8B4513',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: '#6d3610', bgcolor: 'rgba(139,69,19,0.05)' },
                }}
              >
                Learn More
              </Button>
            </Box>

            {/* Stats */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 3, md: 5 },
                mt: 5,
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight="bold" color="#8B4513">
                  50+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Products
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="#8B4513">
                  100%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ml: 1}}>
                  Natural
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Hero Image Slider */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={() => setSlideIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length)}
              size="small"
              sx={{
                bgcolor: 'rgba(139, 69, 19, 0.15)',
                color: '#8B4513',
                '&:hover': { bgcolor: 'rgba(139, 69, 19, 0.3)' },
              }}
              aria-label="Previous image"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Box
              sx={{
                width: { xs: '280px', md: '400px' },
                height: { xs: '280px', md: '400px' },
                borderRadius: '50%',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(139, 69, 19, 0.3)',
                position: 'relative',
              }}
            >
            {HERO_IMAGES.map((img, index) => (
              <Box
                key={img.src}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: index === slideIndex ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  pointerEvents: index === slideIndex ? 'auto' : 'none',
                }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ))}
            {/* Slider dots */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                zIndex: 2,
              }}
            >
              {HERO_IMAGES.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setSlideIndex(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: index === slideIndex ? '#8B4513' : 'rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': { bgcolor: index === slideIndex ? '#6d3610' : 'rgba(255,255,255,0.9)' },
                  }}
                />
              ))}
            </Box>
            </Box>
            <IconButton
              onClick={() => setSlideIndex((prev) => (prev + 1) % HERO_IMAGES.length)}
              size="small"
              sx={{
                bgcolor: 'rgba(139, 69, 19, 0.15)',
                color: '#8B4513',
                '&:hover': { bgcolor: 'rgba(139, 69, 19, 0.3)' },
              }}
              aria-label="Next image"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroBanner;
